<script setup lang="ts">
import { toRefs } from "vue";
import { useProvideTabsState } from "./useTabsState";

interface Props {
  orientation?: "horizontal" | "vertical"
  scrollable?: boolean
  lazy?: boolean
  tile?: boolean
  inkbar?: boolean
  listPosition?: "left" | "right"
  centerItemsInList?: boolean
  dividerBetweenTabs?: boolean
  /**
   * Opt-in fixed width (in px) for tile tabs in vertical orientation, overriding the default
   * 100px tile column. Leave undefined to keep the shared default. Ignored for horizontal tabs.
   */
  verticalTileWidth?: number
  /**
   * `header` is the App Store app header bar: horizontal text tabs with an icon, no gap between them, a 12px
   * inset, the inkbar on a full-width 2px track and no background on the active, hovered or focused tab.
   */
  appearance?: "default" | "header"
}

const props = withDefaults(defineProps<Props>(), {
  orientation: "horizontal",
  scrollable: false,
  lazy: false,
  tile: false,
  inkbar: true,
  listPosition: "left",
  centerItemsInList: false,
  dividerBetweenTabs: false,
  verticalTileWidth: undefined,
  appearance: "default",
});

const modelValue = defineModel<number>({
  required: true,
  default: 0,
});

const {
  orientation,
  scrollable,
  lazy,
  tile,
  inkbar,
  listPosition,
  centerItemsInList,
  dividerBetweenTabs,
  verticalTileWidth,
  appearance,
} = toRefs(props);

useProvideTabsState({
  modelValue,
  orientation,
  scrollable,
  lazy,
  tile,
  inkbar,
  listPosition,
  centerItemsInList,
  dividerBetweenTabs,
  verticalTileWidth,
  appearance,
});
</script>

<template>
  <div
    class="flex w-full h-full min-h-0"
    :class="[
      orientation === 'horizontal'
        ? 'flex-col'
        : `flex-row${listPosition === 'right' ? '-reverse' : ''}`,
    ]"
  >
    <slot />
  </div>
</template>
